import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ReactSVG } from "react-svg";
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import './styles.css';
import { getStyles } from "./styles";
import { IconSvg } from '../../utils/globalIcons';
import { routesConfig } from '../../Routes/Routes';
import { setProjectAccessState, setWorkspaceAccessState } from '../../Redux/slice/dataSlice';
import { useAppContext } from '../../context/AppContext';
import { useCredit } from '../../context/CreditContext';
import { IS_AWS_MODE, IS_COMMUNITY_MODE, LOCAL_TESTING_ENABLED } from '../../utils';

const menuItems = [
  {
    icon: IconSvg.flagIcon,
    label: 'Get Started',
    path: 'home'
  },
  {
    icon: IconSvg.spaceIcon,
    label: 'Spaces',
    path: 'spaces'
  },
  {
    icon: IconSvg.projectIcon,
    label: 'Projects',
    path: 'projects'
  },
  {
    icon: IconSvg.settingsIcon,
    label: 'Settings',
    path: 'settings'
  },
  {
    icon: IconSvg.collectionTypeIcon,
    label: 'Content Models',
    path: 'collection_types'
  },
  {
    icon: IconSvg.fieldsetIcon,
    label: 'Components',
    path: 'fieldsets'
  },
  {
    icon: IconSvg.collectionIcon,
    label: 'Content',
    path: 'collections'
  },
  {
    icon: IconSvg.mediaIcon,
    label: 'Media Library',
    path: 'files'
  },
  {
    icon: IconSvg.appsIcon,
    label: 'Apps',
    path: 'appflyte_apps'
  }
];

function Sidebar() {

  const theme = useTheme();
  const styles = getStyles(theme);
  const location = useLocation();
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { selectedOrganization, selectedService, selectedWorkspace, selectedProject, permissionStatus, setSelectedWorkspace,
    setSelectedProject, setPermissionStatus, billingStatus, setBillingStatus, isOrganizationOwner,
    apiKeyStatus, setApiKeyStatus } = useAppContext();
  const [activeRoute, setActiveRoute] = useState(null);
  const { shouldShowCreditWarning } = useCredit();

  const filteredMenuItems = useMemo(() => {
    let items = [];

    if (!selectedOrganization || !selectedService) {
      items.push(menuItems.find(item => item.path === 'home'));
    }

    if (selectedOrganization && selectedService) {
      items.push(menuItems.find(item => item.path === 'spaces'));
    }

    if (selectedOrganization && selectedService && selectedWorkspace && !selectedProject) {
      items.push(menuItems.find(item => item.path === 'projects'));
    }

    if (selectedOrganization && selectedService && selectedWorkspace && selectedProject) {
      const selected_sidebar_items = selectedProject?.payload?.configuration?.engine_config?.sidebar_items ?? []
      const allowedLabels = new Set(Object.keys(selected_sidebar_items));
      items = menuItems.filter(item => allowedLabels.has(item.path));
    }
    return items;
  }, [selectedOrganization, selectedService, selectedWorkspace, selectedProject]);

  useEffect(() => {
    const currentPath = location.pathname;
    const matchedRoute = routesConfig.find(route => {
      let regexPath = route.path
        .replace(/:\w+/g, '[^/]+')
        .replace(/\/\*$/, '(?:/.*)?');
      const regex = new RegExp(`^${regexPath}$`);
      return regex.test(currentPath);
    });
    if (matchedRoute) {
      setActiveRoute(matchedRoute.component);
    }
  }, [location]);

  useEffect(() => {
    if (location?.pathname === "/user/billing" && !selectedOrganization) {
      navigate('/organizations')
    }

    if (location?.pathname === "/user/billing" && selectedOrganization) {
      setPermissionStatus(false)
      setBillingStatus(true)
      setApiKeyStatus(false)
    }
    //eslint-disable-next-line
  }, [location])


  const handlePageRouting = useCallback((path) => {
    const organization_id = selectedOrganization?.payload?.__auto_id__ ?? null;
    const workspace_id = selectedWorkspace?.payload?.__auto_id__ ?? null;
    const project_id = selectedProject?.payload?.__auto_id__ ?? null;

    switch (path) {
      case 'home':
        navigate('/')
        break;
      case 'spaces':
        navigate(`/organization/${organization_id}/workspaces`)
        break;
      case 'projects':
        navigate(`/organization/${organization_id}/workspace/${workspace_id}/projects`)
        break;
      case 'collection_types':
        navigate(`/workspace/${workspace_id}/project/${project_id}/collection-types`)
        break;
      case 'fieldsets':
        navigate(`/workspace/${workspace_id}/project/${project_id}/field-set`)
        break;
      case 'collections':
        navigate(`/workspace/${workspace_id}/project/${project_id}/collections`)
        break;
      case 'files':
        navigate(`/workspace/${workspace_id}/project/${project_id}/files`)
        break;
      case 'appflyte_apps':
        navigate(`/apps`)
        break;
      case 'settings':
        navigate('/settings')
        break;
      default:
        navigate('/')
    }
  }, [selectedOrganization, selectedWorkspace, selectedProject, navigate]);

  const handleMenuItemClick = useCallback((path) => {
    dispatch(setWorkspaceAccessState({}))
    dispatch(setProjectAccessState({}))
    setPermissionStatus(false)
    setBillingStatus(false)
    setApiKeyStatus(false)
    if (!path) {
      navigate('/')
      return
    }
    handlePageRouting(path)
  }, [dispatch, setPermissionStatus, setBillingStatus, setApiKeyStatus, handlePageRouting, navigate]);

  const handleKeyDown = useCallback((e, path) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMenuItemClick(path);
    }
  }, [handleMenuItemClick]);

  const handlePermissions = () => {
    setBillingStatus(false)
    setPermissionStatus(true)
    setApiKeyStatus(false)
    setSelectedWorkspace(null)
    setSelectedProject(null)
    navigate('/user/settings')
  }

  const handleBilling = () => {
    setPermissionStatus(false)
    setBillingStatus(true)
    setApiKeyStatus(false)
    navigate('/user/billing')
  }

  const isUtilityInactive = !permissionStatus && !billingStatus && !apiKeyStatus;

  return (

    <nav style={styles.sidebar} aria-label="Main navigation">
      <ul className="menu" role="menubar" aria-orientation="vertical">
        {filteredMenuItems?.map((item) => {
          const isActive = activeRoute === item.path && isUtilityInactive;
          return (
            <li key={item.path}
              role="menuitem"
              tabIndex={0}
              className={`menu-item ${isActive ? 'active' : ''}`}
              onClick={() => handleMenuItemClick(item.path)}
              onKeyDown={(e) => handleKeyDown(e, item.path)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="icon" aria-hidden="true">
                <ReactSVG
                  src={item.icon}
                  className={`subsidebar_icon ${isActive ? 'active' : ''}`}
                />
              </span>
              <Typography className="label" variant="body1">{item.label}</Typography>
            </li>
          );
        })}
      </ul>

      <ul className="menu" style={{ flex: 'none', marginBottom: shouldShowCreditWarning ? '30px' : '10px' }}>
        {(selectedOrganization && isOrganizationOwner && !IS_AWS_MODE && !IS_COMMUNITY_MODE) &&
          <li
            role="menuitem"
            tabIndex={0}
            className={`menu-item ${(billingStatus && !permissionStatus && !apiKeyStatus) ? 'active' : ''}`}
            onClick={() => selectedOrganization && handleBilling()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectedOrganization && handleBilling(); } }}
            aria-current={billingStatus ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">
              <ReactSVG
                src={IconSvg.paymentIcon}
                className={`subsidebar_icon ${(billingStatus && !permissionStatus && !apiKeyStatus) ? 'active' : ''}`}
              />
            </span>
            <Typography className="label" variant="body1">Billing & Usage</Typography>
          </li>
        }

        {(selectedOrganization && isOrganizationOwner && !LOCAL_TESTING_ENABLED) &&
          <li
            role="menuitem"
            tabIndex={0}
            className={`menu-item ${(permissionStatus && !billingStatus && !apiKeyStatus) ? 'active' : ''}`}
            onClick={handlePermissions}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePermissions(); } }}
            aria-current={permissionStatus ? 'page' : undefined}
          >
            <span className="icon" aria-hidden="true">
              <ReactSVG
                src={IconSvg.rootusersIcon}
                className={`subsidebar_icon ${(permissionStatus && !billingStatus && !apiKeyStatus) ? 'active' : ''}`}
              />
            </span>
            <Typography className="label" variant="body1">Manage Users</Typography>
          </li>
        }

      </ul>
    </nav>
  );
}

export default Sidebar;
